import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  listPosts,
  createPost,
  updatePost,
  deletePost,
  requestFeedback,
  listPillars,
} from "@/lib/weave";
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Trash2,
  Save,
  X,
} from "lucide-react";

// Date helpers
const startOfWeek = (d) => {
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7; // Mon=0
  dt.setDate(dt.getDate() - day);
  dt.setHours(0, 0, 0, 0);
  return dt;
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const fmt = (d) => d.toISOString().slice(0, 10);
const labelDay = (d) =>
  d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

const STATUS = [
  { v: "draft", label: "Draft", color: "var(--muted)" },
  { v: "scheduled", label: "Scheduled", color: "var(--yellow-deep)" },
  { v: "published", label: "Published", color: "var(--teal)" },
];

function PostEditor({ post, pillars, onSave, onClose, onDelete, onFeedback }) {
  const [draft, setDraft] = useState({ ...post });
  const [busy, setBusy] = useState(false);
  const [feedbackBusy, setFeedbackBusy] = useState(false);

  useEffect(() => setDraft({ ...post }), [post]);

  const save = async () => {
    setBusy(true);
    try {
      await onSave(draft);
      toast.success("Saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const reqFeedback = async () => {
    if (!draft.body || draft.body.trim().length < 30) {
      toast.error("Write at least a draft body before asking the coach.");
      return;
    }
    if (!draft.post_id) {
      // ensure saved
      await save();
    }
    setFeedbackBusy(true);
    try {
      const fb = await onFeedback(draft.post_id);
      setDraft((d) => ({ ...d, ai_feedback: fb }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Coach is unavailable.");
    } finally {
      setFeedbackBusy(false);
    }
  };

  const fb = draft.ai_feedback;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="post-editor">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-[var(--paper)] h-full overflow-y-auto p-6 md:p-8 border-l border-[var(--line)]">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="overline">{draft.post_id ? "Edit post" : "New post"}</div>
            <h2 className="font-display text-2xl tracking-tight mt-1">
              {draft.title || "Untitled draft"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[var(--bg-2)] rounded-sm"
            data-testid="post-editor-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-5">
          <div>
            <label className="overline block mb-2">Title (your reference)</label>
            <Input
              value={draft.title || ""}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="Lessons from launching v2"
              className="rounded-sm border-[var(--line)] h-11"
              data-testid="post-title-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="overline block mb-2">Pillar</label>
              <select
                value={draft.pillar_id || ""}
                onChange={(e) => setDraft({ ...draft, pillar_id: e.target.value || null })}
                className="w-full h-11 rounded-sm border border-[var(--line)] px-3 bg-white"
                data-testid="post-pillar-select"
              >
                <option value="">— No pillar —</option>
                {pillars.map((p) => (
                  <option key={p.pillar_id} value={p.pillar_id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Date</label>
              <Input
                type="date"
                value={draft.scheduled_for || ""}
                onChange={(e) =>
                  setDraft({ ...draft, scheduled_for: e.target.value || null })
                }
                className="rounded-sm border-[var(--line)] h-11"
                data-testid="post-date-input"
              />
            </div>
          </div>

          <div>
            <label className="overline block mb-2">Status</label>
            <div className="flex gap-2">
              {STATUS.map((s) => (
                <button
                  key={s.v}
                  type="button"
                  onClick={() => setDraft({ ...draft, status: s.v })}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                    draft.status === s.v
                      ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                      : "border-[var(--line)] text-[var(--ink-2)]"
                  }`}
                  data-testid={`post-status-${s.v}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="overline block mb-2">Body</label>
            <Textarea
              rows={12}
              value={draft.body || ""}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              placeholder={"Hook (1 line — scroll-stopping)\n\nContext (2-4 lines — the story / why)\n\nPoint (1-2 lines — the insight)\n\nCall to conversation (1 line)"}
              className="rounded-sm border-[var(--line)] font-mono text-[13px] leading-relaxed"
              data-testid="post-body-input"
            />
          </div>

          <div className="flex items-center justify-between gap-3 pt-2 flex-wrap">
            <div className="flex gap-2">
              <Button
                onClick={save}
                disabled={busy}
                className="btn-primary rounded-full h-10 px-5 flex items-center gap-2"
                data-testid="post-save-btn"
              >
                <Save size={14} /> {busy ? "Saving…" : "Save"}
              </Button>
              <Button
                onClick={reqFeedback}
                disabled={feedbackBusy}
                className="btn-accent rounded-full h-10 px-5 flex items-center gap-2"
                data-testid="post-feedback-btn"
              >
                <Sparkles size={14} /> {feedbackBusy ? "Reading…" : "Ask the coach"}
              </Button>
            </div>
            {draft.post_id && (
              <Button
                variant="outline"
                onClick={() => onDelete(draft.post_id)}
                className="rounded-full h-10 text-[var(--red)] border-[var(--line)]"
                data-testid="post-delete-btn"
              >
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
            )}
          </div>

          {fb && (
            <div className="rounded-sm border border-[var(--line)] p-5 bg-[var(--bg-2)] space-y-4" data-testid="post-feedback-block">
              <div className="flex items-center justify-between gap-3">
                <div className="overline" style={{ color: "var(--teal)" }}>
                  Coach feedback
                </div>
                <div className="font-display text-3xl">
                  {fb.overall ?? "?"}
                  <span className="text-xs text-[var(--muted)] font-mono-mark">/10</span>
                </div>
              </div>
              {fb.scores && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  {Object.entries(fb.scores).map(([k, v]) => (
                    <div key={k} className="bg-white rounded-sm p-2 border border-[var(--line)]">
                      <div className="overline">{k}</div>
                      <div className="font-display text-2xl">{v}</div>
                    </div>
                  ))}
                </div>
              )}
              {fb.praise && (
                <p className="text-[var(--ink-2)]"><span className="font-medium">What works: </span>{fb.praise}</p>
              )}
              {Array.isArray(fb.fixes) && fb.fixes.length > 0 && (
                <div>
                  <div className="overline mb-2">Suggested edits</div>
                  <ul className="space-y-2">
                    {fb.fixes.map((x, i) => (
                      <li key={i} className="flex gap-2 text-sm">
                        <span className="text-[var(--teal)]">→</span>
                        <span>{x}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {fb.rewrite && (
                <div>
                  <div className="overline mb-2">Rewrite suggestion</div>
                  <p className="text-[var(--ink-2)] whitespace-pre-line bg-white p-4 border border-[var(--line)] rounded-sm">
                    {fb.rewrite}
                  </p>
                  <Button
                    size="sm"
                    onClick={() => setDraft((d) => ({ ...d, body: fb.rewrite }))}
                    className="btn-yellow rounded-full mt-3 h-9 px-4 text-xs"
                    data-testid="apply-rewrite-btn"
                  >
                    Apply rewrite
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const [posts, setPosts] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [editing, setEditing] = useState(null); // post object or null

  const load = async () => {
    const [p, pl] = await Promise.all([listPosts(), listPillars()]);
    setPosts(p);
    setPillars(pl);
  };
  useEffect(() => {
    load();
  }, []);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const postsByDay = useMemo(() => {
    const map = {};
    days.forEach((d) => (map[fmt(d)] = []));
    posts.forEach((p) => {
      if (p.scheduled_for && map[p.scheduled_for]) map[p.scheduled_for].push(p);
    });
    return map;
  }, [posts, days]);

  const unscheduled = useMemo(
    () => posts.filter((p) => !p.scheduled_for),
    [posts]
  );

  const newPostFor = (dateStr) =>
    setEditing({ title: "", body: "", scheduled_for: dateStr || null, status: "draft" });

  const save = async (draft) => {
    if (draft.post_id) {
      const updated = await updatePost(draft.post_id, {
        title: draft.title,
        body: draft.body,
        pillar_id: draft.pillar_id,
        scheduled_for: draft.scheduled_for,
        status: draft.status,
      });
      setEditing(updated);
    } else {
      const created = await createPost({
        title: draft.title,
        body: draft.body,
        pillar_id: draft.pillar_id,
        scheduled_for: draft.scheduled_for,
        status: draft.status,
      });
      setEditing(created);
    }
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this draft?")) return;
    await deletePost(id);
    setEditing(null);
    load();
  };

  const fb = async (id) => requestFeedback(id);

  const pillarById = (id) => pillars.find((p) => p.pillar_id === id);

  return (
    <div data-testid="calendar-page" className="space-y-10">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Module 07</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
            Calendar &amp; drafts.
          </h1>
          <p className="mt-3 text-[var(--ink-2)] max-w-2xl">
            Visual week view. Draft posts, plot them on a day, and ask the coach for feedback.
          </p>
        </div>
        <Button
          onClick={() => newPostFor(null)}
          className="btn-primary rounded-full h-11 px-5 flex items-center gap-2"
          data-testid="new-post-btn"
        >
          <Plus size={16} /> New post
        </Button>
      </div>

      {/* week nav */}
      <div className="flex items-center justify-between gap-4">
        <button
          onClick={() => setWeekStart(addDays(weekStart, -7))}
          className="p-2 rounded-sm hover:bg-[var(--bg-2)]"
          data-testid="cal-prev"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="font-display text-xl tracking-tight">
          {labelDay(weekStart)} → {labelDay(addDays(weekStart, 6))}
        </div>
        <button
          onClick={() => setWeekStart(addDays(weekStart, 7))}
          className="p-2 rounded-sm hover:bg-[var(--bg-2)]"
          data-testid="cal-next"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* days */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
        {days.map((d) => {
          const k = fmt(d);
          const items = postsByDay[k] || [];
          return (
            <div
              key={k}
              className="card-tech rounded-sm p-3 min-h-[180px] flex flex-col"
              data-testid={`day-${k}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="overline">{d.toLocaleDateString("en-GB", { weekday: "short" })}</div>
                <div className="font-display text-lg">{d.getDate()}</div>
              </div>
              <div className="flex-1 space-y-2">
                {items.map((p) => {
                  const pillar = pillarById(p.pillar_id);
                  const statusColor = STATUS.find((s) => s.v === p.status)?.color || "var(--muted)";
                  return (
                    <button
                      key={p.post_id}
                      onClick={() => setEditing(p)}
                      className="w-full text-left p-2 bg-[var(--bg-2)] rounded-sm hover:bg-[var(--bg)] transition border border-[var(--line)]"
                      data-testid={`post-card-${p.post_id}`}
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusColor }} />
                        <span className="text-[10px] font-mono-mark uppercase tracking-wider text-[var(--muted)]">
                          {p.status}
                        </span>
                      </div>
                      <div className="text-sm font-display tracking-tight line-clamp-2">
                        {p.title || p.body?.slice(0, 60) || "Untitled"}
                      </div>
                      {pillar && (
                        <div className="text-[10px] mt-1 text-[var(--muted)]">{pillar.name}</div>
                      )}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => newPostFor(k)}
                className="text-xs text-[var(--muted)] hover:text-[var(--teal)] mt-2 self-start"
                data-testid={`day-add-${k}`}
              >
                + add
              </button>
            </div>
          );
        })}
      </div>

      {/* unscheduled */}
      {unscheduled.length > 0 && (
        <section>
          <div className="overline mb-3">Unscheduled drafts</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {unscheduled.map((p) => (
              <button
                key={p.post_id}
                onClick={() => setEditing(p)}
                className="card-tech rounded-sm p-4 text-left"
                data-testid={`unscheduled-${p.post_id}`}
              >
                <div className="overline mb-1">Draft</div>
                <div className="font-display text-lg tracking-tight line-clamp-2">
                  {p.title || p.body?.slice(0, 60) || "Untitled"}
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {editing && (
        <PostEditor
          post={editing}
          pillars={pillars}
          onClose={() => setEditing(null)}
          onSave={save}
          onDelete={remove}
          onFeedback={fb}
        />
      )}
    </div>
  );
}
